import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Mail, CalendarClock, Gem, PlayCircle } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, GhostButton, fmtDate } from '../ui';

const Toggle = ({ testId, checked, onChange }) => (
  <button
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`w-12 h-7 rounded-full relative transition-colors flex-shrink-0 ${checked ? 'bg-[#1f3d2b]' : 'bg-gray-300'}`}
  >
    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const AutomationRow = ({ icon: Icon, title, desc, testId, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-[#f0ebe0] last:border-0">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center flex-shrink-0">
        <Icon size={16} className="text-[#1f3d2b]" />
      </div>
      <div>
        <div className="font-semibold text-[#0d1f15]">{title}</div>
        <div className="text-xs text-[#5a7062]">{desc}</div>
      </div>
    </div>
    <Toggle testId={testId} checked={checked} onChange={onChange} />
  </div>
);

const AutomationsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/automations`, { headers: authHeader() })
      .then((res) => setCfg(res.data))
      .catch(() => setError('Could not load automations.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (field, value) => {
    setCfg((c) => ({ ...c, [field]: value }));
    try {
      await axios.patch(`${DASH}/automations`, { [field]: value }, { headers: authHeader() });
    } catch {
      setError('Could not save — reverting.');
      load();
    }
  };

  const runNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const res = await axios.post(`${DASH}/automations/run-now`, {}, { headers: authHeader() });
      setRunResult(res.data.sent);
      load();
    } catch {
      setError('Could not run the sweep.');
    } finally {
      setRunning(false);
    }
  };

  if (loading || !cfg) return <Spinner label="Loading automations…" />;

  return (
    <div data-testid="wellness-admin-section-automations" className="space-y-4">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Automations
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">Automatic email notifications (via Resend). Runs hourly in the background.</p>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <SectionCard
        testId="automations-card"
        actions={
          <GhostButton testId="automations-run-now" onClick={runNow} disabled={running}>
            <PlayCircle size={15} /> {running ? 'Running…' : 'Run Sweep Now'}
          </GhostButton>
        }
      >
        <AutomationRow
          icon={Mail}
          title="Booking Confirmation"
          desc="Sent instantly when a session is paid for or booked with a credit."
          testId="automation-booking-confirmation-toggle"
          checked={cfg.booking_confirmation_enabled}
          onChange={(v) => toggle('booking_confirmation_enabled', v)}
        />
        <AutomationRow
          icon={CalendarClock}
          title="Appointment Reminder"
          desc="Sent ~24 hours before an upcoming paid session."
          testId="automation-appointment-reminder-toggle"
          checked={cfg.appointment_reminder_enabled}
          onChange={(v) => toggle('appointment_reminder_enabled', v)}
        />
        <AutomationRow
          icon={Gem}
          title="Pack Expiry Reminder"
          desc="Sent 7 days and 3 days before a pack/membership's credits expire."
          testId="automation-pack-expiry-reminder-toggle"
          checked={cfg.pack_expiry_reminder_enabled}
          onChange={(v) => toggle('pack_expiry_reminder_enabled', v)}
        />
        <div className="pt-4 text-xs text-[#5a7062]">
          Last sweep: {cfg.last_sweep_at ? fmtDate(cfg.last_sweep_at) : 'never run yet'}
        </div>
        {runResult && (
          <div data-testid="automations-run-result" className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            Sent: {runResult.appointment_reminders} appointment reminder(s), {runResult.pack_reminders_7d} 7-day pack reminder(s), {runResult.pack_reminders_3d} 3-day pack reminder(s).
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default AutomationsSection;
